import { z } from 'zod';

import { apiRequest } from '@/api/client';

/** Schemas mirroring `app/schemas/tenant.py` and `app/schemas/user.py`. */

export const BUSINESS_TYPES = [
  { value: 'FASHION', label: 'Fashion & apparel' },
  { value: 'ELECTRONICS', label: 'Electronics' },
  { value: 'GROCERY', label: 'Grocery & essentials' },
  { value: 'BEAUTY', label: 'Beauty & personal care' },
  { value: 'HOME_LIVING', label: 'Home & living' },
  { value: 'HANDICRAFTS', label: 'Handicrafts' },
  { value: 'FOOD_BEVERAGE', label: 'Food & beverage' },
  { value: 'HEALTH_WELLNESS', label: 'Health & wellness' },
  { value: 'JEWELLERY', label: 'Jewellery' },
  { value: 'SERVICES', label: 'Services' },
  { value: 'OTHER', label: 'Something else' },
] as const;

export const tenantSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  business_type: z.string(),
  /** A key from `industries.ts`; null for a store made before the picker existed. */
  industry: z.string().nullish(),
  email: z.string(),
  phone: z.string().nullable(),
  // The registered address printed on invoices. Nullish so a fixture or an
  // older response without the street lines still parses.
  address_line1: z.string().nullish(),
  address_line2: z.string().nullish(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postal_code: z.string().nullish(),
  country: z.string(),
  gstin: z.string().nullable(),
  // Seller details for product pages. Nullish/defaulted so an older response
  // or a hand-built fixture still parses.
  legal_name: z.string().nullish(),
  fssai_number: z.string().nullish(),
  show_seller_details: z.boolean().default(false),
  currency: z.string(),
  timezone: z.string(),
  status: z.string(),
  onboarding_step: z.string(),
  onboarding_completed_at: z.string().nullable(),
  created_at: z.string(),
});

export const onboardingStatusSchema = z.object({
  current_step: z.string(),
  completed_steps: z.array(z.string()),
  remaining_steps: z.array(z.string()),
  // Also listed in `completed_steps`; kept apart so the checklist can say
  // "Skipped" rather than "Completed". Absent from older API builds.
  skipped_steps: z.array(z.string()).default([]),
  is_complete: z.boolean(),
});

export const staffMemberSchema = z.object({
  id: z.string(),
  email: z.string(),
  full_name: z.string(),
  role: z.string(),
  status: z.string(),
  created_at: z.string(),
});

export const staffPageSchema = z.object({
  items: z.array(staffMemberSchema),
  meta: z.object({
    page: z.number(),
    size: z.number(),
    total: z.number(),
    pages: z.number(),
    has_next: z.boolean(),
    has_previous: z.boolean(),
  }),
});

export type Tenant = z.infer<typeof tenantSchema>;
export type OnboardingStatus = z.infer<typeof onboardingStatusSchema>;
export type StaffMember = z.infer<typeof staffMemberSchema>;

export interface CreateBusinessPayload {
  name: string;
  /** Older, coarser vocabulary; the API derives it from `industry` when that is sent. */
  business_type?: string;
  /** A key from `industries.ts`. */
  industry?: string;
  /** Start the store with the industry's first five categories and sample products. */
  seed_sample_catalog?: boolean;
  /** Optional: left out, the owner's address is used, or none yet. */
  email?: string;
  /** The owner's name, when sign-up (WhatsApp) did not ask for it. */
  owner_name?: string;
  phone?: string;
  city?: string;
  state?: string;
  gstin?: string;
}

export function createBusiness(payload: CreateBusinessPayload): Promise<Tenant> {
  return apiRequest('/tenants', tenantSchema, { method: 'POST', body: payload });
}

export function fetchMyBusiness(): Promise<Tenant> {
  return apiRequest('/tenants/me', tenantSchema);
}

export interface UpdateBusinessPayload {
  /** A key from `INDUSTRIES`. The API re-derives `business_type` from it. */
  industry?: string;
  /** Validated and normalised by the API; an empty string removes it. */
  phone?: string;
  /** Upper-cased by the API; an empty string removes it. */
  gstin?: string;
  /**
   * The registered address. The API ignores nulls, so an empty string is how
   * a line is cleared.
   */
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  /** The registered legal name; an empty string removes it. */
  legal_name?: string;
  /** 14 digits; an empty string removes it. */
  fssai_number?: string;
  /** Publish the seller details on storefront product pages. */
  show_seller_details?: boolean;
}

export function updateMyBusiness(payload: UpdateBusinessPayload): Promise<Tenant> {
  return apiRequest('/tenants/me', tenantSchema, { method: 'PATCH', body: payload });
}

export function fetchOnboardingStatus(): Promise<OnboardingStatus> {
  return apiRequest('/tenants/me/onboarding', onboardingStatusSchema);
}

/** For a business that does not ship: ticks the shipping step off the checklist. */
export function skipShippingStep(): Promise<OnboardingStatus> {
  return apiRequest('/tenants/me/onboarding/skip-shipping', onboardingStatusSchema, {
    method: 'POST',
  });
}

/** Puts the shipping step back on the checklist. */
export function unskipShippingStep(): Promise<OnboardingStatus> {
  return apiRequest('/tenants/me/onboarding/skip-shipping', onboardingStatusSchema, {
    method: 'DELETE',
  });
}

export function fetchStaff(): Promise<z.infer<typeof staffPageSchema>> {
  return apiRequest('/users', staffPageSchema);
}
